import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { EventForm, AGE_WIRE_MAP, WIRE_TO_AGE_MAP } from '../components/events/EventForm';
import type { EventFormValues } from '../components/events/EventForm';
import { EventCancelModal } from '../components/events/EventCancelModal';
import {
  useGetEventDetail,
  useGetEventDraft,
  useUpdateEventDraft,
  useEditPublishedEvent,
  useCreateEvent,
  useCancelEvent,
  useDeleteEventDraft,
  useGetStripeAccountStatus,
  type SingleEventCreationPayload,
  type EventTicketRequest,
  type MediaUrlEntry,
  type MediaInstruction,
  type EventItem,
  type DraftUpdatePayload,
} from '../api/events';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { resolveDominantColorForSubmit, colorsEqual } from '../utils/dominantColor';
import { FiArrowLeft, FiAlertCircle } from 'react-icons/fi';
import { toast } from '../utils/toast';
import { Button } from '../components/ui/Button';
import type { LocationBody } from '../services/locationService';
import { DEFAULT_TIMEZONE } from '../components/ui/TimezoneSelect';
import { extractApiError } from '../utils/apiError';
import { buildGuestPayloadFields } from '../utils/guestInvite';
import { useAuth } from '../hooks/useAuth';
import { canEditEvent } from '../utils/eventPermissions';
import { nowAsPicked, wallClockAsPicked, wallClockFromUtcIso } from '../utils/eventDate';
import { UnauthorizedPage } from '../components/common/UnauthorizedPage';

export const Route = createFileRoute('/events/$eventId/edit')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: location.pathname } });
    }
  },
  component: EditPage,
});

function parseDatetime(iso: string): { date: string; time: string } {
  if (!iso) return { date: '', time: '' };
  return wallClockFromUtcIso(iso);
}

function formatDateDisplay(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

function EditPage() {
  const { eventId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const currentUserId = user?.id ?? localStorage.getItem('user_id') ?? '';
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isDeleteDraftModalOpen, setIsDeleteDraftModalOpen] = useState(false);

  const [draftHint, setDraftHint] = useState<boolean | undefined>(undefined);
  const {
    data: event,
    isLoading: isEventLoading,
    error: eventError,
  } = useGetEventDetail(eventId, draftHint);
  const isDraftEvent = event?.is_draft_or_published === 'draft';

  useEffect(() => {
    if (event?.is_draft_or_published) {
      const isDraft = event.is_draft_or_published === 'draft';
      if (isDraft !== draftHint) {
        setDraftHint(isDraft);
      }
    }
  }, [event?.is_draft_or_published, draftHint]);

  const { data: draftEvent, isLoading: isDraftLoading } = useGetEventDraft(eventId, isDraftEvent);
  const updateDraft = useUpdateEventDraft();
  const editPublishedEvent = useEditPublishedEvent();
  const createEvent = useCreateEvent();
  const cancelEvent = useCancelEvent();
  const deleteDraft = useDeleteEventDraft();
  const { data: stripeAccount } = useGetStripeAccountStatus({ enabled: true });

  const resolvedEvent = isDraftEvent ? (draftEvent ?? event) : event;
  const isLoading = isEventLoading || (isDraftEvent && isDraftLoading);
  const saveMutation =
    resolvedEvent?.is_draft_or_published === 'draft' ? updateDraft : editPublishedEvent;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white pb-32">
        <Header />
        <main className="pt-24 md:pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
        </main>
        <BottomNav />
      </div>
    );
  }

  if (eventError || !resolvedEvent) {
    return (
      <div className="min-h-screen bg-black text-white pb-32">
        <Header />
        <main className="pt-24 md:pt-20 max-w-360 mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center min-h-[60vh] text-center">
          <FiAlertCircle size={48} className="text-zinc-600 mb-4" />
          <h2 className="text-white text-xl font-semibold mb-2">Event not found</h2>
          <p className="text-zinc-500 mb-6">Unable to load event for editing.</p>
          <Button
            onClick={() => navigate({ to: '/events' })}
            size="lg"
            className="rounded-full px-8"
          >
            Back to Your Events
          </Button>
        </main>
        <BottomNav />
      </div>
    );
  }

  if (!canEditEvent(resolvedEvent, currentUserId)) {
    return <UnauthorizedPage resourceType="event" resourceId={eventId} />;
  }

  const start = parseDatetime(resolvedEvent.start ?? '');
  const end = parseDatetime(resolvedEvent.end ?? '');

  const eventDateDisplay = `${formatDateDisplay(resolvedEvent.start ?? '')}${resolvedEvent.end ? ` — ${formatDateDisplay(resolvedEvent.end)}` : ''}`;
  const eventLocation = [resolvedEvent.city, resolvedEvent.state].filter(Boolean).join(', ');

  const handleCancel = () => {
    if (!eventId) return;
    cancelEvent.mutate(Number(eventId), {
      onSuccess: () => {
        setIsCancelModalOpen(false);
        toast.success('Event cancelled');
        queryClient.invalidateQueries({ queryKey: ['events'] });
        queryClient.invalidateQueries({ queryKey: ['events', 'detail', eventId] });
        navigate({ to: '/events' });
      },
      onError: (err) => {
        toast.error(extractApiError(err, 'Failed to cancel event'));
      },
    });
  };

  const handleDeleteDraft = () => {
    if (!eventId) return;
    deleteDraft.mutate(
      { draftId: Number(eventId), draftType: 'single-draft' },
      {
        onSuccess: () => {
          setIsDeleteDraftModalOpen(false);
          toast.success('Draft deleted');
          queryClient.invalidateQueries({ queryKey: ['events'] });
          queryClient.removeQueries({ queryKey: ['events', 'detail', eventId] });
          queryClient.removeQueries({ queryKey: ['events', 'draft', eventId] });
          navigate({ to: '/events' });
        },
        onError: (err) => {
          toast.error(extractApiError(err, 'Failed to delete draft'));
        },
      },
    );
  };

  const parseFormDate = (date: string, time: string): string | undefined => {
    if (!date || !time) return undefined;
    return wallClockAsPicked(date, time);
  };

  const formTicketToApiRequest = (
    t: NonNullable<EventFormValues['tickets']>[number],
    fallbackExpiredAt: string,
    isPublishedEvent: boolean,
  ): EventTicketRequest => {
    const isUpdate = isPublishedEvent && !!t.id && t.id > 0;
    const req: EventTicketRequest = {
      id: t.id ?? 0,
      ticket_type: t.ticket_type,
      price: t.price,
      set_purchase_date: t.has_end_purchase_period ?? false,
      is_multi_event_pass: t.multi_day_access || false,
      start_at:
        t.go_live_on_publish || !t.sales_start_date || !t.sales_start_time
          ? nowAsPicked()
          : parseFormDate(t.sales_start_date, t.sales_start_time),
      expired_at: t.has_end_purchase_period
        ? parseFormDate(t.sales_end_date ?? '', t.sales_end_time ?? '')
        : fallbackExpiredAt,
    };
    if (isUpdate) {
      req.remaining_tickets = t.quantity;
    } else {
      req.ticket_number_issued = t.quantity;
    }
    if (t.description) {
      req.description = t.description;
    }
    if (t.sub_events) {
      req.access_type = 'all_access';
      req.accessible_sub_event_ids = [];
    }
    if (t.go_live_on_publish) {
      req.use_publish_time_for_start_at = true;
    }
    return req;
  };

  interface NormalizedTicket {
    id: number;
    start_at: number;
    expired_at: number;
    access_type: string;
    ticket_type: string;
    price: number;
    ticket_number_issued: number;
    description: string;
    use_publish_time_for_start_at: boolean;
    set_purchase_date: boolean;
    is_multi_event_pass: boolean;
    accessible_sub_event_ids: string;
  }

  // Compare as wall-clock minutes: payload times carry no zone, stored ones are Z-stamped.
  const wallClockMs = (v?: string): number => (v ? new Date(`${v.slice(0, 16)}:00Z`).getTime() : 0);

  const normalizeTicket = (
    t: EventTicketRequest | NonNullable<EventItem['tickets']>[number],
  ): NormalizedTicket => ({
    id: t.id ?? 0,
    start_at: wallClockMs(t.start_at),
    expired_at: wallClockMs(t.expired_at),
    access_type: t.access_type || '',
    ticket_type: t.ticket_type || '',
    price: t.price ?? 0,
    ticket_number_issued:
      (t as EventTicketRequest).ticket_number_issued ??
      (t as EventTicketRequest).remaining_tickets ??
      (t as NonNullable<EventItem['tickets']>[number]).ticket_number_issued ??
      (t as NonNullable<EventItem['tickets']>[number]).remaining_tickets ??
      0,
    description: t.description || '',
    use_publish_time_for_start_at: t.use_publish_time_for_start_at ?? false,
    set_purchase_date: t.set_purchase_date ?? false,
    is_multi_event_pass: t.is_multi_event_pass ?? false,
    accessible_sub_event_ids: t.accessible_sub_event_ids?.length
      ? JSON.stringify(t.accessible_sub_event_ids.sort())
      : '',
  });

  const hasTicketChanged = (a: NormalizedTicket, b: NormalizedTicket): boolean => {
    return (
      a.start_at !== b.start_at ||
      a.expired_at !== b.expired_at ||
      a.access_type !== b.access_type ||
      a.ticket_type !== b.ticket_type ||
      a.price !== b.price ||
      a.ticket_number_issued !== b.ticket_number_issued ||
      a.description !== b.description ||
      a.use_publish_time_for_start_at !== b.use_publish_time_for_start_at ||
      a.set_purchase_date !== b.set_purchase_date ||
      a.is_multi_event_pass !== b.is_multi_event_pass ||
      a.accessible_sub_event_ids !== b.accessible_sub_event_ids
    );
  };

  const normalizeDateTimeForDiff = (value: unknown): string => {
    if (value === undefined || value === null || value === '') return '';
    // Wall-clock minutes: payload times carry no zone, stored ones are Z-stamped.
    return String(value).trim().replace(' ', 'T').slice(0, 16);
  };

  const buildTicketDiff = (
    formTickets: NonNullable<EventFormValues['tickets']>,
    originalTickets: EventItem['tickets'],
    fallbackExpiredAt: string,
    isPublishedEvent: boolean,
  ): EventTicketRequest[] => {
    const originalList = originalTickets ?? [];
    const diff: EventTicketRequest[] = [];

    const currentIds = new Set(formTickets.filter((t) => t.id && t.id > 0).map((t) => t.id));
    for (const orig of originalList) {
      if (orig.id && !currentIds.has(orig.id)) {
        diff.push({
          id: orig.id,
          ticket_type: orig.ticket_type ?? '',
          price: orig.price,
          ticket_number_issued: orig.ticket_number_issued ?? 0,
          delete: true,
        });
      }
    }

    const formApiTickets = formTickets.map((ticket) => {
      const req = formTicketToApiRequest(ticket, fallbackExpiredAt, isPublishedEvent);
      const orig = originalList.find((o) => o.id === ticket.id && ticket.id && ticket.id > 0);
      // go_live_on_publish tickets always compute start_at as "now" since the
      // real value is set server-side at publish time. Reuse the original
      // start_at here when that setting hasn't changed, so unrelated saves
      // (e.g. editing only the title) don't produce a false ticket diff.
      if (orig && ticket.go_live_on_publish && orig.use_publish_time_for_start_at) {
        req.start_at = orig.start_at;
      }
      return req;
    });
    const normalizedOriginals = originalList.map(normalizeTicket);
    const normalizedForms = formApiTickets.map(normalizeTicket);

    const ticketsChanged =
      normalizedOriginals.length !== normalizedForms.length ||
      normalizedForms.some((ft, i) => hasTicketChanged(ft, normalizedOriginals[i]));

    if (!ticketsChanged) {
      return diff;
    }

    for (let i = 0; i < formTickets.length; i++) {
      const ft = formTickets[i];
      const currentReq = formApiTickets[i];
      const orig = originalList.find((o) => o.id === ft.id && ft.id && ft.id > 0);

      if (!orig) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id: _id, ...rest } = currentReq;
        diff.push(rest as EventTicketRequest);
        continue;
      }

      const changes: Record<string, unknown> = { id: orig.id };
      const compare = (key: string, cur: unknown, origVal: unknown) => {
        if (cur === undefined) return;
        const curEmpty = cur === null || cur === '';
        const origEmpty = origVal === undefined || origVal === null || origVal === '';
        if (curEmpty && origEmpty) return;
        if ((key === 'start_at' || key === 'expired_at') && curEmpty) return;
        if (key === 'start_at' || key === 'expired_at') {
          const curNormalized = normalizeDateTimeForDiff(cur);
          const origNormalized = normalizeDateTimeForDiff(origVal);
          if (curNormalized !== origNormalized) changes[key] = cur;
          return;
        }
        if (cur !== origVal) changes[key] = cur;
      };
      compare('ticket_type', currentReq.ticket_type, orig.ticket_type ?? '');
      compare('price', currentReq.price, orig.price);
      compare(
        'remaining_tickets',
        currentReq.remaining_tickets,
        orig.remaining_tickets ?? orig.ticket_number_issued ?? 0,
      );
      compare('description', currentReq.description, orig.description ?? '');
      compare('start_at', currentReq.start_at, orig.start_at ?? '');
      compare('expired_at', currentReq.expired_at, orig.expired_at ?? '');
      compare(
        'use_publish_time_for_start_at',
        currentReq.use_publish_time_for_start_at ?? false,
        orig.use_publish_time_for_start_at ?? false,
      );
      compare(
        'set_purchase_date',
        currentReq.set_purchase_date ?? false,
        orig.set_purchase_date ?? false,
      );
      compare(
        'is_multi_event_pass',
        currentReq.is_multi_event_pass ?? false,
        orig.is_multi_event_pass ?? false,
      );
      compare('access_type', currentReq.access_type, orig.access_type ?? '');
      compare(
        'accessible_sub_event_ids',
        currentReq.accessible_sub_event_ids?.length
          ? JSON.stringify(currentReq.accessible_sub_event_ids)
          : undefined,
        orig.accessible_sub_event_ids?.length
          ? JSON.stringify(orig.accessible_sub_event_ids)
          : undefined,
      );

      if (Object.keys(changes).length > 1) {
        diff.push(changes as unknown as EventTicketRequest);
      }
    }
    return diff;
  };

  const handleSubmit = async (values: EventFormValues, action: 'save' | 'publish') => {
    const hasPaidTicket = (values.tickets ?? []).some((t) => t.price > 0);
    if (action === 'publish' && hasPaidTicket) {
      if (!stripeAccount?.can_receive_payments) {
        toast.error('You must complete Stripe payment setup before publishing paid events.');
        return;
      }
    }

    const newStart = wallClockAsPicked(values.startDate, values.startTime);
    const newEnd = wallClockAsPicked(values.endDate, values.endTime);

    const dominantColor = await resolveDominantColorForSubmit(
      values.images[0],
      values.dominantColor,
    );

    let newMediaUrls: MediaUrlEntry[] = [];
    const newFiles = values.imageFiles.filter((f): f is File => f !== null);
    if (newFiles.length > 0) {
      try {
        const results = await uploadFilesToCloudinary(newFiles, {
          folder: 'event_images',
          uploadPreset: 'event_preset',
        });
        newMediaUrls = results.map((r) => ({
          public_id: r.public_id,
          secure_url: r.secure_url,
          resource_type: r.resource_type,
          bytes: r.bytes,
          format: r.format,
        }));
      } catch {
        toast.error('Failed to upload images. Please try again.');
        return;
      }
    }

    const mediaInstructions: MediaInstruction[] = (values.deletedImageIds ?? []).map((id) => ({
      id,
      delete: true,
    }));

    const isPublished = resolvedEvent?.is_draft_or_published === 'published';
    if (action === 'save') {
      const eventDiff: Record<string, unknown> = {};

      if (values.event_title !== initialData.event_title) {
        eventDiff.event_title = values.event_title;
      }
      if (values.info !== initialData.info) {
        eventDiff.info = values.info;
      }
      const currentAge = AGE_WIRE_MAP[values.event_age] || values.event_age;
      const initialAge = AGE_WIRE_MAP[initialData.event_age ?? ''] || initialData.event_age;
      if (currentAge !== initialAge) {
        eventDiff.event_age = currentAge;
      }

      const initialLoc = initialData.locationBody;
      const locChanged =
        !initialLoc ||
        values.locationBody?.address !== initialLoc.address ||
        values.locationBody?.city !== initialLoc.city ||
        values.locationBody?.state !== initialLoc.state ||
        values.locationBody?.country !== initialLoc.country ||
        values.locationBody?.latitude !== initialLoc.latitude ||
        values.locationBody?.longitude !== initialLoc.longitude;

      if (locChanged) {
        eventDiff.location_body = values.locationBody
          ? {
              street: values.locationBody.address,
              city: values.locationBody.city,
              state: values.locationBody.state,
              country: values.locationBody.country,
              longitude: values.locationBody.longitude,
              latitude: values.locationBody.latitude,
            }
          : { street: '', city: '', state: '', country: '', longitude: 0, latitude: 0 };
      }

      if (
        JSON.stringify([...(values.genres ?? [])].sort()) !==
        JSON.stringify([...(initialData.genres ?? [])].sort())
      ) {
        eventDiff.genres = values.genres;
      }

      if (values.timezone !== initialData.timezone) {
        eventDiff.start = newStart;
        eventDiff.end = newEnd;
        eventDiff.iana_timezone = values.timezone;
      }
      if (
        values.startDate !== initialData.startDate ||
        values.startTime !== initialData.startTime
      ) {
        eventDiff.start = newStart;
      }
      if (values.endDate !== initialData.endDate || values.endTime !== initialData.endTime) {
        eventDiff.end = newEnd;
      }
      if (eventDiff.start !== undefined || eventDiff.end !== undefined) {
        eventDiff.iana_timezone = values.timezone;
      }

      const currentAddress = values.locationDetails || values.locationBody?.address || '';
      const initialAddress = initialData.locationDetails || initialData.locationBody?.address || '';
      if (currentAddress !== initialAddress) {
        eventDiff.address = currentAddress;
      }

      if ((values.is_private ?? false) !== (initialData.is_private ?? false)) {
        eventDiff.is_private = values.is_private;
      }

      if (!colorsEqual(dominantColor, initialData.dominantColor)) {
        eventDiff.dominant_color = dominantColor;
      }

      if (values.is_private) {
        Object.assign(eventDiff, buildGuestPayloadFields(values.guests));
      }

      const ticketDiff = buildTicketDiff(
        values.tickets ?? [],
        resolvedEvent.tickets,
        newEnd,
        isPublished,
      );

      const initialCollabIds = initialData.collaboratorIds ?? [];
      const collaboratorsChanged =
        JSON.stringify([...(values.collaboratorIds ?? [])].sort()) !==
        JSON.stringify([...initialCollabIds].sort());

      if (collaboratorsChanged) {
        eventDiff.collaborators = values.collaboratorIds;
      }

      if (isPublished) {
        const allEvent: Record<string, unknown> = { ...eventDiff };
        if (ticketDiff.length > 0) allEvent.tickets = ticketDiff;

        editPublishedEvent.mutate(
          {
            eventId: Number(eventId),
            payload: {
              event: allEvent,
              ...(newMediaUrls.length > 0 ? { media_urls: newMediaUrls } : {}),
              ...(mediaInstructions.length > 0 ? { media_instructions: mediaInstructions } : {}),
              ...(newFiles.length > 0 ? { images: newFiles } : {}),
            },
          },
          {
            onSuccess: () => {
              toast.success('Event updated successfully!');
              queryClient.invalidateQueries({ queryKey: ['events', 'detail', eventId] });
              queryClient.invalidateQueries({ queryKey: ['events', 'sub-events'] });
              navigate({ to: '/events/$eventId', params: { eventId } });
            },
            onError: (err) => {
              toast.error(extractApiError(err, 'Failed to update event'));
            },
          },
        );
        return;
      } else {
        const draftUpdatePayload: DraftUpdatePayload = {
          event: eventDiff,
          ...(ticketDiff.length > 0 ? { ticket: ticketDiff } : {}),
          ...(newMediaUrls.length > 0 ? { media_urls: newMediaUrls } : {}),
          ...(mediaInstructions.length > 0 ? { media_instructions: mediaInstructions } : {}),
        };

        updateDraft.mutate(
          { eventId: Number(eventId), payload: draftUpdatePayload },
          {
            onSuccess: () => {
              toast.success('Draft saved!');
              queryClient.invalidateQueries({ queryKey: ['events', 'detail', eventId] });
              queryClient.invalidateQueries({ queryKey: ['events', 'draft', eventId] });
              queryClient.invalidateQueries({ queryKey: ['events', 'sub-events'] });
              navigate({ to: '/events/$eventId', params: { eventId } });
            },
            onError: (err) => {
              toast.error(extractApiError(err, 'Failed to update event'));
            },
          },
        );
        return;
      }
    }

    // Build location body once — shared between publish and draft-save paths
    const locationBody = values.locationBody
      ? {
          street: values.locationBody.address,
          city: values.locationBody.city,
          state: values.locationBody.state,
          country: values.locationBody.country,
          longitude: values.locationBody.longitude,
          latitude: values.locationBody.latitude,
        }
      : { street: '', city: '', state: '', country: '', longitude: 0, latitude: 0 };

    if (action === 'publish') {
      // Full payload when promoting a draft to published — all fields required
      const eventPayload: SingleEventCreationPayload = {
        event_title: values.event_title,
        event_type: resolvedEvent.event_type ?? 'physical',
        draft_id: resolvedEvent.is_draft_or_published === 'draft' ? Number(eventId) : undefined,
        event_age: AGE_WIRE_MAP[values.event_age] || values.event_age,
        info: values.info,
        location_body: locationBody,
        genres: values.genres,
        start: newStart,
        end: newEnd,
        sponsors: '',
        email: '',
        address: values.locationDetails || values.locationBody?.address || '',
        collaborators: values.collaboratorIds.length ? values.collaboratorIds : undefined,
        is_private: values.is_private ?? false,
        iana_timezone: values.timezone,
        dominant_color: dominantColor,
      };

      if (values.is_private) {
        Object.assign(eventPayload, buildGuestPayloadFields(values.guests));
      }

      const tickets: EventTicketRequest[] = (values.tickets ?? []).map((ticket) =>
        formTicketToApiRequest(ticket, newEnd, false),
      );
      const existingMediaUrls: MediaUrlEntry[] = values.existingImageIds
        .map((id, i) => ({ id, url: values.images[i] }))
        .filter((x) => x.id != null && x.url)
        .map((x) => ({ public_id: '', secure_url: x.url, resource_type: 'image' }));
      const allMediaUrls = [...existingMediaUrls, ...newMediaUrls];

      createEvent.mutate(
        {
          event: eventPayload,
          ticket: tickets,
          ...(allMediaUrls.length > 0 ? { media_urls: allMediaUrls } : {}),
          ...(mediaInstructions.length > 0 ? { media_instructions: mediaInstructions } : {}),
        },
        {
          onSuccess: (data) => {
            toast.success('Event published!');
            const publishedId = data.event_id ?? data.event?.id;
            queryClient.invalidateQueries({ queryKey: ['events', 'detail', eventId] });
            navigate({
              to: '/events/$eventId',
              params: { eventId: String(publishedId ?? eventId) },
            });
          },
          onError: (err) => {
            toast.error(extractApiError(err, 'Failed to publish event'));
          },
        },
      );
      return;
    }
  };

  const initialData: Partial<EventFormValues> = {
    event_title: resolvedEvent.event_title ?? '',
    info: resolvedEvent.info ?? '',
    startDate: start.date,
    startTime: start.time,
    endDate: end.date,
    endTime: end.time,
    timezone: resolvedEvent.timezone ?? DEFAULT_TIMEZONE,
    locationBody: {
      address: resolvedEvent.venue ?? resolvedEvent.address ?? '',
      city: resolvedEvent.city ?? '',
      state: resolvedEvent.state ?? '',
      country: resolvedEvent.country ?? '',
      latitude: 0,
      longitude: 0,
    } as LocationBody,
    locationDetails: '',
    event_age: WIRE_TO_AGE_MAP[resolvedEvent.event_age ?? '18'] ?? '18+',
    genres: (resolvedEvent.genres ?? [])
      .map((g) => g.id)
      .filter((id): id is number => typeof id === 'number'),
    images: (resolvedEvent.medias ?? []).map((m) => m.file_url ?? '').filter(Boolean),
    imageFiles: (resolvedEvent.medias ?? []).map(() => null),
    existingImageIds: (resolvedEvent.medias ?? []).map((m) => m.id),
    dominantColor: resolvedEvent.dominant_color ?? null,
    tickets: (resolvedEvent.tickets ?? []).map((t) => {
      const salesStart = parseDatetime(t.start_at ?? '');
      const salesEnd = parseDatetime(t.expired_at ?? '');
      return {
        id: t.id,
        ticket_type: t.ticket_type ?? '',
        price: t.price,
        quantity: t.remaining_tickets ?? t.ticket_number_issued ?? 0,
        ticket_number_issued: t.ticket_number_issued ?? 0,
        remaining_tickets: t.remaining_tickets ?? 0,
        description: t.description,
        sales_start_date: salesStart.date,
        sales_start_time: salesStart.time,
        sales_end_date: salesEnd.date,
        sales_end_time: salesEnd.time,
        has_end_purchase_period: t.set_purchase_date ?? false,
        go_live_on_publish: t.use_publish_time_for_start_at ?? false,
        sub_events: !!t.accessible_sub_event_ids?.length,
        multi_day_access: t.is_multi_event_pass ?? false,
      };
    }),
    is_private: resolvedEvent.is_private ?? false,
    collaboratorIds: (resolvedEvent.collaborators ?? []).map((c) => c.id),
    is_draft_or_published:
      (resolvedEvent.is_draft_or_published as 'draft' | 'published') ?? 'draft',
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />
      <main className="pt-24 md:pt-20">
        <div className="max-w-360 mx-auto px-4 sm:px-6 lg:px-8 mb-2">
          <button
            onClick={() => window.history.back()}
            className="p-2 hover:bg-white/5 rounded-full transition-colors group"
          >
            <FiArrowLeft
              size={24}
              className="text-zinc-400 group-hover:text-white transition-colors"
            />
          </button>
        </div>
        <EventForm
          mode="edit"
          title="Edit Event"
          statusBadge={resolvedEvent.is_draft_or_published as 'draft' | 'published'}
          {...(resolvedEvent?.is_draft_or_published !== 'draft'
            ? {
                secondaryActionLabel: 'Cancel Event',
                onSecondaryAction: () => setIsCancelModalOpen(true),
              }
            : {
                secondaryActionLabel: 'Delete Draft',
                onSecondaryAction: () => setIsDeleteDraftModalOpen(true),
              })}
          onSubmit={handleSubmit}
          initialData={initialData}
          isLoading={saveMutation.isPending}
          hidePrivateToggle={
            resolvedEvent.event_structure_type === 'multi_event' || !!resolvedEvent.is_sub_event
          }
          ticketsRequired={
            resolvedEvent.event_structure_type !== 'multi_event' && !resolvedEvent.is_sub_event
          }
        />
      </main>
      <BottomNav />
      {isCancelModalOpen && (
        <EventCancelModal
          eventTitle={resolvedEvent.event_title ?? ''}
          eventDate={eventDateDisplay}
          eventLocation={eventLocation}
          ticketsSold={0}
          eventImage={resolvedEvent.medias?.[0]?.file_url}
          onConfirm={handleCancel}
          onCancel={() => setIsCancelModalOpen(false)}
          isLoading={cancelEvent.isPending}
        />
      )}
      {isDeleteDraftModalOpen && (
        <EventCancelModal
          variant="delete"
          eventTitle={resolvedEvent.event_title ?? ''}
          eventDate={eventDateDisplay}
          eventLocation={eventLocation}
          ticketsSold={0}
          eventImage={resolvedEvent.medias?.[0]?.file_url}
          onConfirm={handleDeleteDraft}
          onCancel={() => setIsDeleteDraftModalOpen(false)}
          isLoading={cancelEvent.isPending}
        />
      )}
    </div>
  );
}
